import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/shared/services/api';
import { endpoints } from '@/shared/services/endpoints';
import { sseClient } from '@/shared/services/sse-client';
import { queryKeys } from '@/shared/services/query-keys';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useNotificationsStore } from '@/shared/stores/notifications-store';
import { useUIStore } from '@/shared/stores/ui-store';
import { NotificationType } from '@/shared/types/enums';
import type { Notification } from '@/shared/types/entities';

/**
 * Notification types that trigger a toast on arrival.
 */
const TOAST_NOTIFICATION_TYPES = new Set<NotificationType>([
  NotificationType.CAMPAIGN_APPROVED,
  NotificationType.CAMPAIGN_REJECTED,
  NotificationType.BUDGET_ALERT,
]);

function getToastVariant(type: NotificationType): 'success' | 'error' | 'warning' | 'info' {
  switch (type) {
    case NotificationType.CAMPAIGN_APPROVED:
      return 'success';
    case NotificationType.CAMPAIGN_REJECTED:
      return 'error';
    case NotificationType.BUDGET_ALERT:
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Establishes an SSE connection for real-time notifications.
 *
 * - Connects to the notifications stream when the user has an orgId.
 * - Dispatches incoming notifications to the Zustand store.
 * - Invalidates TanStack Query notification caches.
 * - Shows toast notifications for important event types.
 * - Disconnects on unmount or when orgId changes.
 */
export function useSSENotifications(): void {
  const orgId = useAuthStore((s) => s.orgId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const addNotification = useNotificationsStore((s) => s.addNotification);
  const setUnreadCount = useNotificationsStore((s) => s.setUnreadCount);
  const addToast = useUIStore((s) => s.addToast);
  const queryClient = useQueryClient();

  // Use refs to avoid stale closures in the SSE handler
  const addNotificationRef = useRef(addNotification);
  const addToastRef = useRef(addToast);
  const setUnreadCountRef = useRef(setUnreadCount);
  addNotificationRef.current = addNotification;
  addToastRef.current = addToast;
  setUnreadCountRef.current = setUnreadCount;

  // Fetch initial unread count and sync to store
  useEffect(() => {
    if (!orgId || !isAuthenticated) return;

    async function fetchInitialUnreadCount(organizationId: string) {
      try {
        const { data } = await apiService.get<{ meta: { unreadCount: number } }>(
          endpoints.notifications.unreadCount(organizationId),
        );
        setUnreadCountRef.current(data.meta.unreadCount);
      } catch {
        // Silently fail -- the count will update on next SSE event
      }
    }

    void fetchInitialUnreadCount(orgId);
  }, [orgId, isAuthenticated]);

  useEffect(() => {
    if (!orgId || !isAuthenticated) return undefined;

    sseClient.connect(orgId);

    const unsubscribe = sseClient.on('message', (event: MessageEvent) => {
      let notification: Notification;
      try {
        notification = JSON.parse(event.data as string) as Notification;
      } catch {
        return;
      }

      // Update the Zustand store with the new notification
      addNotificationRef.current(notification);

      // Invalidate TanStack Query caches so lists and counts refresh
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });

      // Show toast for important notification types
      if (TOAST_NOTIFICATION_TYPES.has(notification.type)) {
        addToastRef.current({
          title: notification.title,
          message: notification.message,
          variant: getToastVariant(notification.type),
        });
      }
    });

    return () => {
      unsubscribe();
      sseClient.disconnect();
    };
  }, [orgId, isAuthenticated, queryClient]);
}
